import { createContext, type FC, type PropsWithChildren, use, useEffect, useId, useMemo, useState } from 'react';

import { formatPageTitle } from './PageTitle.utils';

interface PageTitleRegistration {
  id: string;
  title?: string;
}

interface PageTitleContextValue {
  registerTitle: (registration: PageTitleRegistration) => void;
  unregisterTitle: (id: string) => void;
}

const PageTitleContext = createContext<PageTitleContextValue | null>(null);

/**
 * Owns the document's single declarative title and lets nested entity pages
 * replace route-level fallbacks after their existing data has loaded.
 */
export const PageTitleProvider: FC<PropsWithChildren> = ({ children }) => {
  const [registrations, setRegistrations] = useState<Array<PageTitleRegistration>>([]);

  const contextValue = useMemo<PageTitleContextValue>(
    () => ({
      registerTitle: (registration) => {
        setRegistrations((currentRegistrations) => {
          const existingIndex = currentRegistrations.findIndex(({ id }) => id === registration.id);
          if (existingIndex === -1) {
            return [...currentRegistrations, registration];
          }
          return currentRegistrations.map((currentRegistration, index) =>
            index === existingIndex ? registration : currentRegistration
          );
        });
      },
      unregisterTitle: (id) => {
        setRegistrations((currentRegistrations) =>
          currentRegistrations.reduce<Array<PageTitleRegistration>>((remainingRegistrations, registration) => {
            if (registration.id !== id) {
              remainingRegistrations.push(registration);
            }
            return remainingRegistrations;
          }, [])
        );
      },
    }),
    []
  );

  const activeTitle = registrations.at(-1)?.title;

  return (
    <PageTitleContext value={contextValue}>
      <title>{formatPageTitle(activeTitle)}</title>
      {children}
    </PageTitleContext>
  );
};

interface PageTitleProps {
  title?: string;
}

/**
 * Registers a route or entity title without writing to document.title.
 * The deepest mounted registration wins and automatically restores its parent fallback on unmount.
 */
const PageTitle: FC<PageTitleProps> = ({ title }) => {
  const context = use(PageTitleContext);
  const id = useId();

  useEffect(() => {
    context?.registerTitle({ id, title });
  }, [context, id, title]);

  useEffect(() => () => context?.unregisterTitle(id), [context, id]);

  return null;
};

export default PageTitle;
